/**
 * Opportunities — master spec §5.3, §6.
 *
 * Three things this file refuses on purpose, each because the alternative is a
 * silent wrong number rather than an error:
 *
 *  - **No value fields.** `annual_revenue_customer`, `annual_revenue_net`,
 *    `weighted_revenue` and `lifetime_value` come from
 *    `v_opportunity_effective` and are never written. A body carrying one is a
 *    `400` naming `opportunity_products` as the place to edit (Phase 3).
 *  - **No `close_date`.** It lives on the closure row and nowhere else
 *    (§5.3 [R3]).
 *  - **No `opp_number`.** Generated, and immutable once set (§5.3).
 */

import { z } from 'zod';
import {
  channelTypeSchema,
  forecastCategorySchema,
  leadSourceSchema,
  lineTypeSchema,
  opportunityStatusSchema,
  outcomeSchema,
  priceBasisSchema,
  realizationStatusSchema,
  regionSchema,
  segmentSchema,
  stageCodeSchema,
} from './enums.js';
import { exportAttestationSchema, patternWarningEnvelopeFields } from './export-control.js';
import { opportunityFlagFilterSchema, opportunityFlagWriteSchema } from './flags.js';
import { accountScopeSchema } from './account-scope.js';
import {
  countryCode,
  dateString,
  mediumText,
  money,
  nonNegativeMoney,
  shortText,
  timestampString,
  uuid,
} from './fields.js';
import { dataEnvelope, paginationQuerySchema } from './http.js';

/**
 * Program life, in years — `docs/bugs.md` session 2 part 1, decisions 10/16.
 *
 * **3 for every deal**, whatever its segment or part ("7 years for tester" was
 * dropped). The same number is the column default
 * (`1788481150000_program-years-default.cjs`), what the importer writes on a
 * new deal, and what the new-opportunity form starts at, so a deal created by
 * any of the three doors has a lifetime value. A value someone set is never
 * overwritten by any of them.
 */
export const DEFAULT_PROGRAM_YEARS = 3;

/**
 * What the Program life dropdown offers. The column still accepts 1–30
 * (`opportunities_program_years_check`), so a deal already carrying more than
 * ten keeps its value and the dropdown shows it as an extra option.
 */
export const PROGRAM_LIFE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

/**
 * The columns of `opportunities` a client may set.
 *
 * `owner_user_id` and `fae_user_id` are here because they are ordinary columns
 * — but they are **settable only by `Manager` or `Admin`** (§2), which is a
 * route check, not a schema one: a schema cannot see the caller's tier.
 */
export const opportunityWritableSchema = z.object({
  name: z.string().min(1).max(300),
  account_id: uuid,
  program_id: uuid.nullable().optional(),
  owner_user_id: uuid.optional(),
  fae_user_id: uuid.nullable().optional(),
  rep_firm_id: uuid.nullable().optional(),
  rep_contact_id: uuid.nullable().optional(),

  stage_code: stageCodeSchema.optional(),
  status: opportunityStatusSchema.optional(),
  forecast_category: forecastCategorySchema.optional(),
  channel_type: channelTypeSchema.optional(),
  region: regionSchema,
  segment: segmentSchema.optional(),
  lead_source: leadSourceSchema.optional(),
  price_basis: priceBasisSchema.optional(),

  end_product: shortText.nullable().optional(),
  application: shortText.nullable().optional(),
  design_registration_ref: shortText.nullable().optional(),

  probability_override: z.number().int().min(0).max(100).nullable().optional(),

  created_date: dateString.optional(),
  expected_decision_date: dateString.nullable().optional(),
  expected_first_revenue_date: dateString.nullable().optional(),
  expected_ramp_date: dateString.nullable().optional(),
  actual_ramp_date: dateString.nullable().optional(),

  program_years: z.number().int().min(1).max(30).nullable().optional(),
  nre_amount: nonNegativeMoney.nullable().optional(),
  prototype_revenue_amount: nonNegativeMoney.nullable().optional(),
  gross_margin_pct: z.number().min(-100).max(100).nullable().optional(),

  next_step: shortText.nullable().optional(),
  next_step_due_date: dateString.nullable().optional(),

  on_hold_reason: mediumText.nullable().optional(),
  on_hold_review_date: dateString.nullable().optional(),

  end_use_country: countryCode.nullable().optional(),
});

/**
 * Creation. **The attestation is required in the request body** (§8 item 1) and
 * the refusal is server-side: `403 EXPORT_BLOCKED`, and no record is created.
 *
 * `status` is absent deliberately — a new opportunity is `open`, and reaching
 * a terminal status goes through `POST /:id/stage` or `POST /:id/close` so the
 * closure row is written in the same transaction (§6.2, §6.3).
 */
export const createOpportunitySchema = opportunityWritableSchema
  .omit({ status: true })
  .merge(opportunityFlagWriteSchema)
  .merge(exportAttestationSchema);

export type CreateOpportunity = z.infer<typeof createOpportunitySchema>;

/**
 * Update. Every field optional; `status` still excluded for the same reason.
 *
 * `is_focus` and `is_verified` (session 4) ride on the same request, so a
 * Manager ticks a box with an ordinary `PUT` — but they are **settable only by
 * a Manager or an Admin**, and a User's request carrying either is refused with
 * 403 rather than having the field dropped. Like the assignment columns, that
 * is a route check, because a schema cannot see the caller's tier.
 */
export const updateOpportunitySchema = opportunityWritableSchema
  .omit({ status: true })
  .merge(opportunityFlagWriteSchema)
  .partial()
  .refine((body) => Object.keys(body).length > 0, 'an update must change something');

export type UpdateOpportunity = z.infer<typeof updateOpportunitySchema>;

/**
 * The derived columns a write must NOT carry. Naming them explicitly is what
 * lets the API answer "edit the product lines" instead of ignoring the field
 * and returning a record whose value did not change (Phase 3).
 */
export const DERIVED_OPPORTUNITY_FIELDS = [
  'annual_revenue_customer',
  'annual_revenue_net',
  'weighted_revenue',
  'lifetime_value',
  'effective_probability',
  'realization_status',
  'days_in_current_stage',
  'last_customer_contact_at',
  'first_won_close_date',
  'business_units',
  'primary_units',
  'primary_asp',
  'opp_number',
  'close_date',
] as const;

// ---------------------------------------------------------------------------
// Stage transitions (§6.1, §6.2)
// ---------------------------------------------------------------------------

/**
 * `POST /:id/stage`.
 *
 * **This endpoint does not write history — the trigger does** (§6.12). The
 * note reaches the trigger through `app.stage_change_note`.
 *
 * `closure` is required when the move lands on `DESIGN_WIN`, because §6.2
 * makes that move close the commercial opportunity as won, and §5.5's deferred
 * constraint trigger will refuse the commit without a matching closure row.
 */
export const advanceStageSchema = z.object({
  stage_code: stageCodeSchema,
  note: mediumText.optional(),
  /** §6.1: an Admin may override a failed gate, with a recorded reason. */
  override_reason: shortText.optional(),
  closure: z
    .object({
      close_date: dateString,
      primary_reason_code: z.string().min(1).max(64),
      secondary_reason_codes: z.array(z.string().min(1).max(64)).max(5).optional(),
      /**
       * On the CUSTOMER basis in the request; the server converts to net and
       * stamps `net_factor_at_close` (§5.5 [R3] point 1). A client that sent a
       * net figure would double-convert, which is exactly the mixed-basis
       * defect that table exists to prevent.
       */
      customer_annual_revenue: nonNegativeMoney,
      competitor_id: uuid.nullable().optional(),
      competitor_part_number: shortText.nullable().optional(),
      price_delta_pct: z.number().min(-999).max(999).nullable().optional(),
      what_would_have_changed_it: mediumText.nullable().optional(),
      lessons_learned: mediumText.nullable().optional(),
    })
    .optional(),
});

export type AdvanceStage = z.infer<typeof advanceStageSchema>;

/** What a failed gate returns in `details` (§6.1). */
export const stageGateFailureSchema = z.object({
  stage_code: z.string(),
  missingFields: z.array(z.string()),
  entry_criteria: z.string().nullable(),
});

export type StageGateFailure = z.infer<typeof stageGateFailureSchema>;

// ---------------------------------------------------------------------------
// Closing and reopening (§6.3)
// ---------------------------------------------------------------------------

/**
 * `POST /:id/close`. Requires `close_date`, `outcome` and
 * `primary_reason_code`; a win also requires the revenue.
 *
 * A **won** deal may be re-closed as `lost`: that appends a NEW closure row at
 * the next sequence and the win survives (§6.2).
 */
export const closeOpportunitySchema = z
  .object({
    outcome: outcomeSchema,
    close_date: dateString,
    primary_reason_code: z.string().min(1).max(64),
    secondary_reason_codes: z.array(z.string().min(1).max(64)).max(5).optional(),
    /** Customer basis in, net basis stored, factor stamped (§5.5). */
    customer_annual_revenue: nonNegativeMoney.optional(),
    competitor_id: uuid.nullable().optional(),
    competitor_part_number: shortText.nullable().optional(),
    price_delta_pct: z.number().min(-999).max(999).nullable().optional(),
    what_would_have_changed_it: mediumText.nullable().optional(),
    lessons_learned: mediumText.nullable().optional(),
    note: mediumText.optional(),
  })
  .refine((b) => b.outcome !== 'won' || b.customer_annual_revenue !== undefined, {
    message: 'a win requires customer_annual_revenue',
    path: ['customer_annual_revenue'],
  })
  /**
   * Decision 12: closing as Invalid requires a reason. The reason code says
   * "not a real opportunity"; the note says why, and lands on the append-only
   * stage-history row the status change writes. Required because the deal then
   * disappears from every view, and the note is all a later reader has.
   */
  .refine((b) => b.outcome !== 'invalid' || (b.note ?? '').trim() !== '', {
    message: 'closing as Invalid requires a note saying why it is not a real opportunity',
    path: ['note'],
  });

export type CloseOpportunity = z.infer<typeof closeOpportunitySchema>;

/**
 * `POST /:id/reopen`. **Writes nothing to `opportunity_closures`** — it sets
 * `status = 'open'` and is audited. The history stays, and a later re-close
 * appends (§6.3).
 */
export const reopenOpportunitySchema = z.object({
  note: mediumText.optional(),
});

/** `POST /:id/transfer` — `Manager` or `Admin` only, always audited (§2 rule 2). */
export const transferOpportunitySchema = z
  .object({
    owner_user_id: uuid.optional(),
    fae_user_id: uuid.nullable().optional(),
    note: mediumText.optional(),
  })
  .refine(
    (b) => b.owner_user_id !== undefined || b.fae_user_id !== undefined,
    'a transfer must move the owner, the FAE, or both',
  );

// ---------------------------------------------------------------------------
// Lines and contacts
// ---------------------------------------------------------------------------

/** The largest `annual_units` a line may carry, calculated or typed. */
export const MAX_ANNUAL_UNITS = 1_000_000_000_000;

/**
 * EAU from units per board × boards per year — `docs/bugs.md` session 2 part
 * 3, decision 11, which **reverses FR-0001** (`docs/bugs-fixed.md`) on the
 * owner's instruction of 2026-10-10.
 *
 * When both are set, `annual_units` IS their product: the server writes it on
 * create and update and refuses a request that carries a different one, and
 * the form shows it read-only with the working. When either is null — which is
 * every imported line, since the channel supplies EAU alone — this returns
 * null and `annual_units` is typed as before.
 *
 * Zero is a value, not "unset": a `design_support` line with zero boards has
 * an EAU of zero (§5.3).
 */
export function calculatedAnnualUnits(
  unitsPerBoard: number | null | undefined,
  annualBoards: number | null | undefined,
): number | null {
  if (unitsPerBoard === null || unitsPerBoard === undefined) return null;
  if (annualBoards === null || annualBoards === undefined) return null;
  if (!Number.isFinite(unitsPerBoard) || !Number.isFinite(annualBoards)) return null;
  return unitsPerBoard * annualBoards;
}

const opportunityProductFields = z.object({
  opportunity_id: uuid,
  product_id: uuid,
  /** Optional only when both of the pair are sent: the server calculates it then. */
  annual_units: z.number().int().min(0).max(MAX_ANNUAL_UNITS).optional(),
  asp: nonNegativeMoney,
  customer_target_asp: nonNegativeMoney.nullable().optional(),
  units_per_board: z.number().int().min(0).max(1_000_000).nullable().optional(),
  annual_boards: z.number().int().min(0).max(MAX_ANNUAL_UNITS).nullable().optional(),
  line_type: lineTypeSchema.optional(),
});

/**
 * A new line. `annual_units` is required unless both `units_per_board` and
 * `annual_boards` are sent, in which case it is their product and may be
 * omitted. Whether a sent `annual_units` agrees with the pair is checked by the
 * route, which on an update also has the stored row to merge with.
 */
export const opportunityProductSchema = opportunityProductFields.superRefine((body, ctx) => {
  if (
    body.annual_units === undefined &&
    calculatedAnnualUnits(body.units_per_board, body.annual_boards) === null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['annual_units'],
      message: 'Required unless units_per_board and annual_boards are both set.',
    });
  }
});

export const updateOpportunityProductSchema = opportunityProductFields
  .omit({ opportunity_id: true, product_id: true })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'an update must change something');

export const opportunityContactSchema = z.object({
  opportunity_id: uuid,
  contact_id: uuid,
  buying_role: z.string().max(40).nullable().optional(),
  influence_level: z.string().max(20).nullable().optional(),
  disposition: z.string().max(20).nullable().optional(),
});

export const updateOpportunityContactSchema = opportunityContactSchema
  .omit({ opportunity_id: true, contact_id: true })
  .partial()
  .refine((b) => Object.keys(b).length > 0, 'an update must change something');

// ---------------------------------------------------------------------------
// The list view (Phase 3, "Counting, precisely")
// ---------------------------------------------------------------------------

/**
 * Filters.
 *
 * **`closingBefore` is `expected_decision_date` before a date** ([R3]).
 * `close_date` lives on `opportunity_closures` and does not exist on
 * `opportunities`, so the original filter had no column to read — and a filter
 * on *closed* deals is not what a pipeline list wants anyway.
 */
export const opportunityFilterSchema = z.object({
  stage: z.string().max(400).optional(),
  status: z.string().max(200).optional(),
  ownerId: uuid.optional(),
  faeId: uuid.optional(),
  /** The account and every live account under it (session 6, `account-scope.ts`). */
  accountId: uuid.optional(),
  accountScope: accountScopeSchema.optional(),
  /**
   * Phase 4's program page lists every opportunity attached to a programme,
   * and a programme spans accounts (§2 rule 3) — so neither `accountId` nor a
   * client-side filter over one page can produce that list correctly.
   * `program_id` is a base column of `opportunities`, so this filter costs the
   * fast count path nothing.
   */
  programId: uuid.optional(),
  repFirmId: uuid.optional(),
  businessUnit: z.string().max(100).optional(),
  segment: z.string().max(200).optional(),
  region: z.string().max(40).optional(),
  forecastCategory: z.string().max(100).optional(),
  priceBasis: z.string().max(40).optional(),
  /** A stall predicate name, or `any`. */
  stalled: z.string().max(60).optional(),
  closingBefore: dateString.optional(),
  rampBetween: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD..YYYY-MM-DD')
    .optional(),
  minValue: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .optional(),
  maxValue: z
    .string()
    .regex(/^\d+(\.\d+)?$/)
    .optional(),
  /** Focus and Verified, `yes` or `no` (session 4) — the same keys analytics takes. */
  ...opportunityFlagFilterSchema.shape,
});

export const listOpportunitiesQuerySchema = paginationQuerySchema.merge(opportunityFilterSchema);
export type ListOpportunitiesQuery = z.infer<typeof listOpportunitiesQuerySchema>;

/** §9: free-text search is `POST` with the term in the BODY, never the query string. */
export const searchOpportunitiesSchema = opportunityFilterSchema
  .merge(z.object({ term: z.string().min(1).max(200) }))
  .merge(
    z.object({
      page: z.number().int().min(1).optional(),
      pageSize: z.number().int().min(1).max(200).optional(),
      sort: z.string().max(64).optional(),
    }),
  );

/**
 * Every `sort` value `GET /api/opportunities` (and `/search`) accepts — the one
 * list both ends read.
 *
 * Sorting is wired in two places: a `sortKey` on a column in the web's
 * `columns.tsx`, and an ORDER BY for that key in the API's `SORTABLE`. A key on
 * one side only is the half-connected case — the header renders a button, the
 * request carries the key, and the API falls back to its default order without
 * saying so. The web types `sortKey` as this union and the API types `SORTABLE`
 * as a `Record` over it, so neither side can add or drop a key alone; a test on
 * each side asserts the same at runtime.
 *
 * `created_date` and `updated_at` are accepted but not shown as columns.
 */
export const OPPORTUNITY_SORT_KEYS = [
  'name',
  'opp_number',
  'account',
  'stage',
  'status',
  'owner',
  'fae',
  'rep_firm',
  'forecast_category',
  'segment',
  'region',
  'next_step',
  'risk',
  'business_units',
  'created_date',
  'expected_ramp_date',
  'expected_decision_date',
  'updated_at',
  'annual_revenue_net',
  'annual_revenue_customer',
  'weighted_revenue',
  'lifetime_value',
  'effective_probability',
  'days_in_current_stage',
  'focus',
  'verified',
] as const;
export type OpportunitySortKey = (typeof OPPORTUNITY_SORT_KEYS)[number];

/** The seven stall predicates, as the list view names them (§5.8). */
export const STALL_PREDICATES = [
  'stalled_stage_days',
  'stalled_no_contact',
  'stalled_decision_date',
  'stalled_next_step',
  'stalled_overdue_action',
  'stalled_win_not_ramping',
  'stalled_hold_review',
] as const;

export type StallPredicate = (typeof STALL_PREDICATES)[number];

/**
 * One row of the list, straight from `v_opportunity_effective`.
 *
 * Money arrives as a string because `node-pg` returns `numeric` as one and §9
 * forbids the `setTypeParser` override that would turn it into a float.
 */
export const opportunityRowSchema = z
  .object({
    id: uuid,
    opp_number: z.string(),
    name: z.string(),
    account_id: uuid,
    account_name: z.string().nullable(),
    owner_user_id: uuid.nullable(),
    fae_user_id: uuid.nullable(),
    stage_code: z.string(),
    stage_name: z.string(),
    stage_sort_order: z.number().int(),
    status: opportunityStatusSchema,
    forecast_category: forecastCategorySchema,
    region: regionSchema,
    segment: segmentSchema,
    price_basis: priceBasisSchema,
    annual_revenue_customer: money,
    annual_revenue_net: money,
    weighted_revenue: money,
    lifetime_value: money.nullable(),
    effective_probability: z.number().int(),
    realization_status: realizationStatusSchema,
    days_in_current_stage: z.number().int().nullable(),
    business_units: z.array(z.string()),
    expected_ramp_date: dateString.nullable(),
    actual_ramp_date: dateString.nullable(),
    expected_decision_date: dateString.nullable(),
    last_customer_contact_at: timestampString.nullable(),
    first_won_close_date: dateString.nullable(),
  })
  .catchall(z.unknown());

export type OpportunityRow = z.infer<typeof opportunityRowSchema>;

/** A mutation's envelope: the record, any pattern warnings, any concurrency notice. */
export const opportunityMutationResponseSchema = dataEnvelope(
  z.object({ opportunity: opportunityRowSchema }).extend(patternWarningEnvelopeFields),
);
